/**
 * App-wide keyboard shortcuts for power users.
 * Triggers ONLY when no input/textarea/contenteditable element is focused.
 *
 * Shortcuts:
 *   E → Record Expense
 *   I → Record Income
 *   T → Transfer
 *   J → Journal Adjustment
 *   N → New Note
 *   ? → Show shortcut help
 *
 * Mounted once in DashboardLayout so every authenticated page gets them.
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Keyboard } from '@phosphor-icons/react';

const SHORTCUTS = [
  { key: 'E', label: 'Record Expense', go: '/dashboard/vouchers?action=expense' },
  { key: 'I', label: 'Record Income', go: '/dashboard/vouchers?action=income' },
  { key: 'T', label: 'Transfer', go: '/dashboard/vouchers?action=transfer' },
  { key: 'J', label: 'Journal Adjustment', go: '/dashboard/accounts/journal' },
  { key: 'N', label: 'New Note', go: '/dashboard/notes' },
  { key: '?', label: 'Show this help', go: null },
];

const isTypingTarget = (el) => {
  if (!el) return false;
  const tag = (el.tagName || '').toUpperCase();
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
  if (el.isContentEditable) return true;
  // Also bail inside any open dialog/modal — power-user flow happens between pages
  if (el.closest && el.closest('[role="dialog"]')) return true;
  return false;
};

export default function GlobalKeyboardShortcuts() {
  const navigate = useNavigate();
  const [helpOpen, setHelpOpen] = useState(false);

  useEffect(() => {
    const onKey = (e) => {
      if (e.metaKey || e.ctrlKey || e.altKey) return; // don't intercept browser shortcuts
      if (isTypingTarget(e.target)) return;
      const k = e.key.toUpperCase();
      const match = SHORTCUTS.find(s => s.key === k || s.key === e.key);
      if (!match) return;
      e.preventDefault();
      if (match.key === '?') {
        setHelpOpen(true);
      } else if (match.go) {
        navigate(match.go);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [navigate]);

  return (
    <AnimatePresence>
      {helpOpen && (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 z-[70] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setHelpOpen(false)}
          data-testid="shortcuts-help-modal"
        >
          <motion.div
            initial={{ scale: 0.95, y: 10 }} animate={{ scale: 1, y: 0 }}
            onClick={(e) => e.stopPropagation()}
            className="bg-card border rounded-xl shadow-2xl w-full max-w-sm overflow-hidden"
          >
            <div className="px-4 py-3 border-b flex items-center justify-between bg-muted/30">
              <div className="flex items-center gap-2">
                <Keyboard size={18} weight="duotone" className="text-primary" />
                <span className="font-semibold text-sm">Keyboard Shortcuts</span>
              </div>
              <button onClick={() => setHelpOpen(false)} className="p-1 rounded hover:bg-foreground/10" data-testid="shortcuts-help-close">
                <X size={16} />
              </button>
            </div>
            <ul className="p-3 space-y-1.5 text-sm">
              {SHORTCUTS.map(s => (
                <li key={s.key} className="flex justify-between items-center px-2 py-1.5 rounded hover:bg-muted/30">
                  <span className="text-muted-foreground">{s.label}</span>
                  <kbd className="px-2 py-0.5 rounded bg-muted text-foreground border font-mono text-xs">{s.key}</kbd>
                </li>
              ))}
            </ul>
            <div className="px-4 py-2 border-t bg-muted/20 text-[11px] text-muted-foreground">
              Shortcuts are disabled while typing in a form field.
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
